import { Link, useSearchParams } from "react-router-dom";
import { Card } from "../../../components";
import { formatImpact } from "../../../utils/format";
import type { PortfolioSummaryGroup } from "../api/portfolio";

interface Props {
  group: PortfolioSummaryGroup;
  groupBy: string;
}

function formatLabel(groupBy: string, value: string | null): string {
  if (!value) return "(none)";

  // Tech type labels
  const techLabels: Record<string, string> = {
    solar_ground: "Solar (Ground)",
    solar_rooftop: "Solar (Rooftop)",
    wind_onshore: "Wind (Onshore)",
    wind_offshore: "Wind (Offshore)",
  };

  if (groupBy === "tech" && value in techLabels) return techLabels[value];
  if (groupBy === "lifecycle") return value === "existing" ? "Operating Projects" : "Prospective Projects";

  return value;
}

export function PortfolioGroupCard({ group, groupBy }: Props) {
  const [searchParams] = useSearchParams();
  const mwDisplay = group.total_mw >= 1000
    ? `${(group.total_mw / 1000).toFixed(1)} GW`
    : `${Math.round(group.total_mw)} MW`;

  // Preserve existing filters and add the new one for nested drill-down
  const nextParams = new URLSearchParams(searchParams);
  nextParams.set(`filter[${groupBy}]`, group.group_value || "");
  const filterParam = nextParams.toString();

  return (
    <Card hover>
      <div className="flex items-center justify-between p-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-body-sm font-semibold text-text-primary truncate">
            {formatLabel(groupBy, group.group_value)}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-body-sm text-text-secondary">
            <span>
              <strong className="text-text-primary">{group.project_count}</strong> project{group.project_count !== 1 ? "s" : ""}
            </span>
            <span>
              <strong className="text-text-primary">{mwDisplay}</strong>
            </span>
            <span className={group.dalys < 0 ? "text-amber-400" : "text-health-400"}>
              {formatImpact(group.dalys, "dalys")} DALYs
            </span>
            <span className={group.pdfs < 0 ? "text-amber-400" : "text-ecosystem-400"}>
              {formatImpact(group.pdfs, "pdfs")} PDFs
            </span>
            <span className={group.usd < 0 ? "text-amber-400" : "text-economic-400"}>
              {formatImpact(group.usd, "usd")}
            </span>
          </div>
        </div>
        <Link
          to={`/app/dashboard?${filterParam}`}
          onClick={(e) => e.stopPropagation()}
          className="ml-4 flex-shrink-0 text-body-sm text-quantum-400 hover:text-quantum-300 transition-colors"
        >
          View All &rarr;
        </Link>
      </div>
    </Card>
  );
}
